<template>
  <section class="login-page" :class="{ 'is-dark': isDark }">
    <!-- Unified Login Notification -->
    <login-notification
      :visible="notification.visible"
      :message="notification.message"
      :type="notification.type"
      :error="notification.error"
      :duration="notification.duration"
      @unlock="handleUnlock"
      @close="clearNotification"
    />

    <div class="login-container">
      <div class="login-header">
        <h3 class="login-title">
          <span>CyRadar Security Information & Event Management</span>
        </h3>
      </div>

      <div class="login-card">
        <!-- Left Side - Login Form -->
        <div class="login-left">

          <!-- Login Form -->
          <form method="POST" @submit.prevent="handleLogin" class="login-form">
            <!-- Username Field -->
            <div class="field">
              <label class="label">{{ $t('authentication.username') }}</label>
              <div class="control">
                <input
                  ref="usernameInput"
                  v-model="username"
                  class="input"
                  :class="{ 'is-danger': usernameError }"
                  type="text"
                  :placeholder="$t('authentication.username')"
                  autocomplete="username"
                  :disabled="isFormDisabled"
                  @blur="validateUsername"
                  @input="clearUsernameError"
                />
              </div>
              <p v-if="usernameError" class="help is-danger">{{ usernameError }}</p>
            </div>

            <!-- Password Field -->
            <div class="field">
              <label class="label">{{ $t('authentication.password') }}</label>
              <div class="control password-control">
                <input
                  ref="passwordInput"
                  v-model="password"
                  class="input"
                  :class="{ 'is-danger': passwordError }"
                  :type="showPassword ? 'text' : 'password'"
                  :placeholder="$t('authentication.password')"
                  autocomplete="current-password"
                  :disabled="isFormDisabled"
                  @blur="validatePassword"
                  @input="clearPasswordError"
                />
                <button
                  type="button"
                  class="password-toggle"
                  @click="togglePasswordVisibility"
                  :disabled="isFormDisabled"
                  :title="showPassword ? 'Hide password' : 'Show password'"
                >
                  <svg v-if="showPassword" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                  <svg v-else xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                </button>
              </div>
              <p v-if="passwordError" class="help is-danger">{{ passwordError }}</p>
            </div>

            <!-- Submit Button -->
            <div class="field">
              <div class="control">
                <button
                  class="button is-primary is-fullwidth"
                  :disabled="processing || isFormDisabled"
                  :class="{ 'is-loading': processing }"
                >
                  {{ $t('authentication.submit') }}
                </button>
              </div>
            </div>
          </form>
        </div>

        <!-- Right Side - Logo and Controls -->
        <div class="login-right">
          <div class="login-right-content">
            <!-- Logo -->
            <div class="login-logo">
              <img src="/logo.png" alt="CyRadar" />
            </div>

            <!-- Language and Theme Controls -->
            <div class="login-controls">
              <div class="control-item">
                <span class="control-label">{{ $t('language.label') }}</span>
                <language-selector />
              </div>
              <div class="control-item">
                <span class="control-label">{{ $t('theme.label') }}</span>
                <theme-toggle />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Copyright Footer -->
      <p v-if="copyright" class="copyright">
        <small v-html="$t('copyrightFooter', { year: currentYear })"></small>
      </p>
    </div>
  </section>
</template>

<script>
import LoginNotification from '../components/LoginNotification.vue'
import LanguageSelector from '../components/LanguageSelector.vue'
import ThemeToggle from '../components/ThemeToggle.vue'

export default {
  name: 'LoginPage',
  components: {
    LoginNotification,
    LanguageSelector,
    ThemeToggle
  },
  data() {
    return {
      username: '',
      password: '',
      showPassword: false,
      usernameError: '',
      passwordError: '',
      processing: false,
      notification: {
        visible: false,
        message: '',
        type: 'info',
        error: null,
        duration: 3000
      }
    }
  },
  computed: {
    /**
     * Whether dark theme is enabled
     */
    isDark() {
      return this.$store.state.ui.dark
    },


    /**
     * Whether the user is authenticated
     */
    authenticated() {
      return this.$store.getters['authentication/authenticated']
    },

    /**
     * Whether the token is expired
     */
    expired() {
      return this.$store.getters['authentication/expired']
    },

    /**
     * Current year for copyright
     */
    currentYear() {
      return new Date().getFullYear()
    },

    /**
     * Copyright configuration
     */
    copyright() {
      return false
    },

    /**
     * Whether the form should be disabled (during account lockout)
     */
    isFormDisabled() {
      return this.notification.error &&
             this.notification.error.code === 423 &&
             this.notification.error.accountLocked &&
             typeof this.notification.error.accountLocked === 'object'
    }
  },

  mounted() {
    // Show expired token warning if applicable
    if (this.expired) {
      this.showNotification(
        'Expired Access Token',
        'warning',
        30000
      )
    }
  },

  methods: {
    /**
     * Validates username/email field
     */
    validateUsername() {
      if (!this.username || this.username.trim() === '') {
        this.usernameError = this.$t('authentication.validation.usernameRequired')
        return false
      }

      // Check if it looks like an email
      if (this.username.includes('@')) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(this.username)) {
          this.usernameError = this.$t('authentication.validation.emailInvalid')
          return false
        }
      }

      this.usernameError = ''
      return true
    },

    /**
     * Validates password field
     */
    validatePassword() {
      if (!this.password || this.password.trim() === '') {
        this.passwordError = this.$t('authentication.validation.passwordRequired')
        return false
      }

      this.passwordError = ''
      return true
    },

    /**
     * Clears username error
     */
    clearUsernameError() {
      this.usernameError = ''
    },

    /**
     * Clears password error
     */
    clearPasswordError() {
      this.passwordError = ''
    },

    /**
     * Handles the login form submission
     */
    async handleLogin() {
      // Validate inputs before submission
      const isUsernameValid = this.validateUsername()
      const isPasswordValid = this.validatePassword()

      if (!isUsernameValid || !isPasswordValid) {
        // Focus on the first invalid input
        this.focusFirstInvalidInput(isUsernameValid, isPasswordValid)
        return
      }

      // Clear any existing notifications
      this.clearNotification()

      // Set processing state
      this.processing = true

      try {
        const response = await this.$http.post('/login', {
          username: this.username,
          password: this.password
        })

        const data = response?.data?.data || response?.data || {}
        const statusCode = data.status_code || response?.status || 200

        if (statusCode === 200) {
          this.handleLoginSuccess(data)
        } else {
          this.handleLoginError(statusCode, data)
        }
      } catch (error) {
        const data = error.response?.data?.data || error.response?.data || {}
        const statusCode = data.status_code || error.response?.status

        this.handleLoginError(statusCode, data, error?.message || error?.toString())
      } finally {
        this.processing = false
      }
    },

    /**
     * Handles successful login
     * @param {Object} data - Response data containing token
     */
    handleLoginSuccess(data) {
      // Check if 2FA is required
      if (data.requires_2fa && data.pending_token) {
        // Store pending token and redirect to 2FA verification
        sessionStorage.setItem('pending_2fa_token', data.pending_token)
        this.$router.push({
          path: '/2fa-verify',
          query: { token: data.pending_token }
        }).catch(err => console.error('Navigation error:', err))
        return
      }

      // Original login success handling (when 2FA is not required)
      if (!data.token) return

      try {
        const tokenParts = data.token.split('.')
        if (tokenParts.length !== 3) return

        const jwtClaims = JSON.parse(atob(tokenParts[1]))
        const user = {
          id: jwtClaims.sub || jwtClaims.user_id || this.username,
          name: jwtClaims.name || jwtClaims.username || this.username,
          expired_at: parseInt(jwtClaims.exp),
          scopes: jwtClaims.scopes || []
        }

        // Clear all authentication notifications before setting new state
        this.$store.dispatch('READ_NOTIFICATIONS_FROM', 'authentication')

        this.$store.commit('authentication/AUTHENTICATION_SET_TOKEN', data.token)
        this.$store.commit('authentication/AUTHENTICATION_SET_USER', user)
        this.$store.dispatch('NOTIFY', {
          type: 'success',
          text: this.$t('authentication.success'),
          duration: 3000
        })

        this.$router.push('/').catch(err => console.error('Navigation error:', err))
      } catch (e) {
        console.error('Failed to parse JWT token:', e)
      }
    },

    /**
     * Handles login errors based on status code
     * @param {number} statusCode - HTTP status code
     * @param {Object} data - Response data
     * @param {string} fallbackMessage - Fallback error message
     */
    handleLoginError(statusCode, data, fallbackMessage = '') {
      const errorHandlers = {
        404: () => this.showNotification(this.$t('authentication.errors.userNotFound'), 'error', 5000),
        401: () => this.handleWrongPassword(data),
        423: () => this.handleAccountLocked(data),
        403: () => this.showNotification(this.$t('authentication.errors.accountDeactivated'), 'error', 5000)
      }

      const handler = errorHandlers[statusCode]
      if (handler) {
        handler()
      } else {
        this.showNotification(
          data.message || fallbackMessage || this.$t('authentication.errors.generalError'),
          'error',
          5000
        )
      }
    },

    /**
     * Handles wrong password error with attempt tracking
     * @param {Object} data - Response data
     */
    handleWrongPassword(data) {
      const remainingAttempts = data.remaining_attempts

      if (remainingAttempts !== undefined && remainingAttempts !== null) {
        this.showNotificationWithError({
          code: 401,
          loginAttempts: {
            failed: data.failed_attempts || 1,
            remaining: remainingAttempts
          },
          message: this.$t('authentication.errors.wrongPassword')
        }, 0)
      } else {
        this.showNotification(this.$t('authentication.errors.wrongPassword'), 'error', 5000)
      }
    },

    /**
     * Handles account locked error with countdown
     * @param {Object} data - Response data
     */
    handleAccountLocked(data) {
      this.showNotificationWithError({
        code: 423,
        accountLocked: {
          lockedUntil: data.locked_until,
          unlockTimeRemaining: data.unlock_time_remaining
        },
        message: data.message || this.$t('authentication.errors.accountLocked')
      }, 0)
    },

    /**
     * Handles when account lockout countdown reaches zero
     */
    handleUnlock() {
      this.clearNotification()
    },

    /**
     * Shows a simple notification
     */
    showNotification(message, type = 'info', duration = 3000) {
      this.notification = {
        visible: true,
        message,
        type,
        error: null,
        duration
      }
    },

    /**
     * Shows a notification with error object (for advanced notifications)
     */
    showNotificationWithError(error, duration = 0) {
      this.notification = {
        visible: true,
        message: '',
        type: 'error',
        error,
        duration
      }
    },

    /**
     * Clears the notification
     */
    clearNotification() {
      this.notification = {
        visible: false,
        message: '',
        type: 'info',
        error: null,
        duration: 3000
      }
    },

    /**
     * Toggles password visibility
     */
    togglePasswordVisibility() {
      this.showPassword = !this.showPassword
    },

    /**
     * Focus on the first invalid input field
     * @param {boolean} isUsernameValid - Whether username is valid
     * @param {boolean} isPasswordValid - Whether password is valid
     */
    focusFirstInvalidInput(isUsernameValid, isPasswordValid) {
      this.$nextTick(() => {
        if (!isUsernameValid && this.$refs.usernameInput) {
          this.$refs.usernameInput.focus()
        } else if (!isPasswordValid && this.$refs.passwordInput) {
          this.$refs.passwordInput.focus()
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
// Color variables
$light-bg: #6b8cae;
$dark-bg: #2b3544;
$card-light-bg: #ffffff;
$card-dark-bg: #1f2430;
$border-light: rgba(0, 0, 0, 0.1);
$border-dark: rgba(255, 255, 255, 0.1);
$text-light: #363636;
$text-dark: #e8ebf1;
$label-light: #363636;
$label-dark: #e8ebf1;

.login-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: $light-bg;
  padding: 2rem;
  transition: background-color 0.3s ease;

  &.is-dark {
    background-color: $dark-bg;
  }
}

.login-container {
  width: 100%;
  max-width: 700px; /* Increased by 10% from 600px */
}

.login-card {
  background: $card-light-bg;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  overflow: visible;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  transition: background-color 0.3s ease;

  .is-dark & {
    background: $card-dark-bg;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  }
}

.login-left {
  padding: 1.8rem; /* Reduced by 10% from 2rem */
  border-right: 1px solid $border-light;
  border-radius: 8px 0 0 8px;
  background: inherit;

  .is-dark & {
    border-right-color: $border-dark;
  }
}

.login-header {
  margin-bottom: 1.35rem; /* Reduced by 10% from 1.5rem */
}

.login-title {
  font-size: 1.75rem;
  white-space: nowrap;
  font-weight: 600;
  color: $text-light;
  margin: 0;

  .is-dark & {
    color: $text-dark;
  }
}

.login-form {
  .field {
    margin-bottom: 0.9rem; /* Reduced by 10% from 1rem */

    &:last-child {
      margin-bottom: 0;
      margin-top: 1.35rem; /* Reduced by 10% from 1.5rem */
    }
  }

  .label {
    font-size: 0.875rem;
    font-weight: 600;
    color: $label-light;
    margin-bottom: 0.5rem;
    display: block;

    .is-dark & {
      color: $label-dark;
    }
  }

  .password-control {
    position: relative;
  }

  .password-toggle {
    position: absolute;
    right: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    cursor: pointer;
    padding: 0.25rem;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #7a7a7a;
    transition: color 0.15s ease;

    &:hover:not(:disabled) {
      color: #363636;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }

    .is-dark & {
      color: #9a9a9a;

      &:hover:not(:disabled) {
        color: #e8ebf1;
      }
    }

    svg {
      display: block;
    }
  }

  .input {
    width: 100%;
    padding: 0.5rem 0.75rem;
    font-size: 0.875rem;
    border: 1px solid #dbdbdb;
    border-radius: 4px;
    background-color: #ffffff;
    color: $text-light;
    transition: border-color 0.15s ease;

    &:focus {
      outline: none;
      border-color: #3273dc;
      box-shadow: 0 0 0 0.125em rgba(50, 115, 220, 0.25);
    }

    &.is-danger {
      border-color: #f14668;

      &:focus {
        border-color: #f14668;
        box-shadow: 0 0 0 0.125em rgba(241, 70, 104, 0.25);
      }
    }

    &:disabled {
      background-color: #f5f5f5;
      cursor: not-allowed;
    }

    .is-dark & {
      background-color: #2b3040;
      border-color: #3a4156;
      color: $text-dark;

      &::placeholder {
        color: rgba(232, 235, 241, 0.6);
      }

      &:focus {
        border-color: #3a7afe;
        box-shadow: 0 0 0 0.125em rgba(58, 122, 254, 0.25);
      }

      &:disabled {
        background-color: #1f2430;
      }
    }
  }

  .password-control .input {
    padding-right: 2.5rem; /* Add space for the toggle button */
  }

  .help {
    font-size: 0.75rem;
    margin-top: 0.25rem;
    display: block;

    &.is-danger {
      color: #f14668;
    }

    .is-dark &.is-danger {
      color: #ff6b9d;
    }
  }

  .button {
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
    font-weight: 600;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.15s ease, transform 0.1s ease;

    &.is-primary {
      background-color: #3273dc;
      color: #ffffff;

      &:hover:not(:disabled) {
        background-color: #2366d1;
      }

      &:active:not(:disabled) {
        transform: translateY(1px);
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      &.is-loading {
        position: relative;
        color: transparent;
        pointer-events: none;

        &::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 1rem;
          height: 1rem;
          margin-top: -0.5rem;
          margin-left: -0.5rem;
          border: 2px solid #ffffff;
          border-right-color: transparent;
          border-radius: 50%;
          animation: spin 0.5s linear infinite;
        }
      }
    }

    &.is-fullwidth {
      width: 100%;
    }
  }
}

.login-right {
  padding: 1.8rem; /* Reduced by 10% from 2rem */
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8f9fa;
  border-radius: 0 8px 8px 0;

  .is-dark & {
    background-color: #252b3a;
  }
}

.login-right-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.35rem; /* Reduced by 10% from 1.5rem */
  width: 100%;
}

.login-logo {
  display: flex;
  justify-content: center;
  align-items: center;

  img {
    max-width: 150px;
    max-height: 60px;
    height: auto;
  }
}

.login-controls {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  align-items: flex-start;

  .control-item {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;
  }

  .control-label {
    display: block;
    font-size: 0.75rem;
    font-weight: 500;
    color: $label-light;
    white-space: nowrap;

    .is-dark & {
      color: $label-dark;
    }
  }
}

.copyright {
  text-align: center;
  margin-top: 2rem;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.875rem;

  .is-dark & {
    color: rgba(255, 255, 255, 0.6);
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

// Responsive design for mobile devices
@media screen and (max-width: 768px) {
  .login-page {
    padding: 1rem;
  }

  .login-card {
    grid-template-columns: 1fr;
  }

  .login-left {
    border-right: none;
    border-bottom: 1px solid $border-light;

    .is-dark & {
      border-bottom-color: $border-dark;
    }
  }

  .login-right {
    padding: 1.5rem;
  }

  .login-right-content {
    gap: 1rem;
  }

  .login-logo img {
    max-width: 120px;
  }
}

// Responsive design for tablet devices
@media screen and (min-width: 769px) and (max-width: 1023px) {
  .login-card {
    max-width: 700px;
  }
}
</style>
